/**
 * Check if user has required permissions for a route
 * @param {Object} route - Route configuration
 * @param {Object} permissions - Permission object from usePermissions()
 */
export const hasRoutePermissions = (route, permissions) => {
  // No permissions required - allow access
  if (!route.requiredPermissions || route.requiredPermissions.length === 0) {
    return true;
  }

  // Platform admin has all permissions
  if (permissions.isPlatformAdmin) {
    return true;
  }

  // Check if user has any of the required permissions
  return route.requiredPermissions.some(permission => 
    permissions.can(permission)
  );
};

/**
 * Flattens nested route configuration into a single array
 */
export const flattenRoutes = (routeConfig) => {
  const routes = [];

  Object.values(routeConfig).forEach((route) => {
    if (route.hasSubMenu && route.subMenu) {
      routes.push(...route.subMenu);
    } else if (route.component) {
      routes.push(route);
    }
  });

  return routes;
};

/**
 * Gets sidebar menu items from route configuration
 * ✅ Now accepts permissions as parameter instead of using hook
 */
export const getSidebarItems = (routeConfig, permissions) => {
  return Object.values(routeConfig)
    .filter((route) => {
      // Must be marked for sidebar
      if (!route.sidebar) return false;
      
      // ✅ Check permissions
      if (!hasRoutePermissions(route, permissions)) return false;
      
      return true;
    })
    .map((route) => {
      let link = route.path;

      // For wildcard routes, use the base path without the wildcard
      if (route.path.includes("*")) {
        link = route.path.replace("/*", "");
      }

      const item = {
        title: route.title,
        link: route.hasSubMenu ? null : link,
        icon: route.icon,
        hasSubMenu: route.hasSubMenu || false,
        badge: route.badge || null,
      };

      // ✅ Filter submenu items by permissions
      if (route.hasSubMenu && route.subMenu) {
        item.subMenu = route.subMenu
          .filter((subRoute) => {
            if (!subRoute.sidebar) return false;
            return hasRoutePermissions(subRoute, permissions);
          })
          .map((sub) => ({
            title: sub.title,
            link: sub.path,
          }));
        
        // If no submenu items are accessible, hide parent
        if (item.subMenu.length === 0) {
          return null;
        }
      } else {
        item.subMenu = [];
      }

      return item;
    })
    .filter(Boolean); // Remove null items
};

/**
 * Finds the current route configuration based on pathname
 */
export const getRouteConfig = (pathname, routeConfig) => {
  return findCurrentRoute(pathname, routeConfig);
};

/**
 * Finds the current route configuration based on pathname
 */
export const findCurrentRoute = (pathname, routeConfig) => {
  const allRoutes = flattenRoutes(routeConfig);

  // Step 1: Check for EXACT matches first
  let currentRoute = allRoutes.find(
    (route) => route.exact && pathname === route.path
  );

  if (currentRoute) {
    console.log("Found exact match:", currentRoute.title);
    return currentRoute;
  }

  // Step 2: Check for DYNAMIC routes (with :param)
  const dynamicRoutes = allRoutes.filter(
    (route) => route.path.includes(":") && !route.path.includes("*")
  );

  for (const route of dynamicRoutes) {
    const pattern = route.path
      .replace(/:[^/]+/g, "[^/]+")
      .replace(/\//g, "\\/");

    const regex = new RegExp(`^${pattern}$`);

    if (regex.test(pathname)) {
      console.log("Found dynamic match:", route.title);
      return route;
    }
  }

  // Step 3: Check for WILDCARD routes last
  const wildcardRoutes = allRoutes.filter((route) => route.path.includes("*"));

  for (const route of wildcardRoutes) {
    const basePath = route.path.replace("/*", "");

    if (pathname.startsWith(basePath)) {
      console.log("Found wildcard match:", route.title);
      return route;
    }
  }

  console.log("No route found for:", pathname);
  return null;
};

/**
 * Gets the active tab name from pathname
 */
export const getActiveTab = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length > 1 ? segments[1] : "dashboard";
};

/**
 * Gets page title with breadcrumb support
 */
export const getPageTitle = (pathname, routeConfig) => {
  const currentRoute = findCurrentRoute(pathname, routeConfig);

  if (!currentRoute) return "Admin";

  // Priority 1: Use navbarTitle if explicitly set
  if (currentRoute.navbarTitle) {
    return currentRoute.navbarTitle;
  }

  // Priority 2: Use titleDisplay option
  if (currentRoute.titleDisplay) {
    switch (currentRoute.titleDisplay) {
      case "current":
        return currentRoute.title;

      case "parent":
        if (currentRoute.parentPath) {
          const parentRoute = findCurrentRoute(
            currentRoute.parentPath,
            routeConfig
          );
          return parentRoute ? parentRoute.title : currentRoute.title;
        }
        return currentRoute.title;

      case "breadcrumb":
        if (currentRoute.parentPath) {
          const parentRoute = findCurrentRoute(
            currentRoute.parentPath,
            routeConfig
          );
          if (parentRoute) {
            return `${parentRoute.title} / ${currentRoute.title}`;
          }
        }
        return currentRoute.title;

      case "custom":
        return currentRoute.customTitle || currentRoute.title;

      default:
        return currentRoute.title;
    }
  }

  // Special handling for Orders with tabs
  if (currentRoute.tabs) {
    const activeTab = currentRoute.tabs.find((tab) => pathname === tab.path);
    if (activeTab) {
      return currentRoute.title;
    }
    return currentRoute.title;
  }

  return currentRoute.title;
};